import React, { useState } from 'react';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from '../components/styled/Card';
import { PrimaryButton, SecondaryButton, SuccessButton } from '../components/styled/Button';
import { Container, Main, Flex, Grid } from '../components/styled/Layout';
import { Table, TableContainer, TableHeader, TableHeaderCell, TableBody, TableRow, TableCell } from '../components/styled/Table';

const AdminApps = () => {
  const { apps, activeApp, switchApp, loading } = useApp();
  const { user } = useAuth();
  const [selectedApp, setSelectedApp] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSwitchApp = async (appId) => {
    try {
      setError('');
      setSuccess('');
      const result = await switchApp(appId);
      if (result.success) {
        setSuccess(`Successfully switched to ${appId}`);
      } else {
        setError(result.error || 'Failed to switch app');
      }
    } catch (error) {
      setError('An unexpected error occurred');
    }
  };

  const getAppIcon = (appId) => {
    const icons = {
      'demo-app': '🚀',
      'crm-system': '👥',
      'email-manager': '📧'
    };
    return icons[appId] || '📱';
  };

  const getAppColor = (appId) => {
    const colors = {
      'demo-app': '#007bff',
      'crm-system': '#28a745',
      'email-manager': '#17a2b8'
    };
    return colors[appId] || '#6c757d';
  };

  return (
    <Container>
      <Main>
        <Flex direction="column" gap="var(--space-5)">
          <Flex direction="column" align="center" gap="var(--space-2)" style={{ textAlign: 'center' }}>
            <h1 style={{ 
              fontSize: '2.5rem', 
              fontWeight: 'var(--font-weight-bold)', 
              color: 'var(--text-primary)',
              margin: 0
            }}>
              🚀 Application Management Center
            </h1>
            <p style={{ 
              fontSize: 'var(--font-size-body)', 
              color: 'var(--text-secondary)',
              margin: 0
            }}>
              Manage and switch between applications in {window.location.hostname}
            </p>
          </Flex>

          {error && (
            <div style={{ 
              padding: 'var(--space-3)', 
              backgroundColor: 'var(--section-error-bg)', 
              border: '1px solid var(--section-error-border)', 
              borderRadius: 'var(--border-radius-small)',
              color: 'var(--text-error-dark)'
            }}>
              {error}
            </div>
          )}

          {success && (
            <div style={{ 
              padding: 'var(--space-3)', 
              backgroundColor: 'var(--section-success-bg)', 
              border: '1px solid var(--state-success)', 
              borderRadius: 'var(--border-radius-small)',
              color: 'var(--text-success-dark)'
            }}>
              {success}
            </div>
          )}

          <Grid style={{ gridTemplateColumns: '1fr 400px', gap: 'var(--space-8)' }}>
            <Card>
              <CardHeader>
                <CardTitle>📱 Available Applications</CardTitle>
              </CardHeader>
              <CardContent>
                <Grid className="apps">
                  {apps.length > 0 ? (
                    apps.map((app) => (
                      <Card 
                        key={app.id} 
                        style={{ 
                          cursor: 'pointer',
                          borderColor: selectedApp?.id === app.id ? 'var(--brand-primary)' : getAppColor(app.id),
                          backgroundColor: app.active ? 'var(--ui-surface-hover-alt)' : 'var(--ui-surface)'
                        }}
                        onClick={() => setSelectedApp(app)}
                      >
                        <Flex align="center" gap="var(--space-3)">
                          <div style={{ 
                            fontSize: '2rem', 
                            color: getAppColor(app.id),
                            minWidth: '60px',
                            textAlign: 'center'
                          }}>
                            {getAppIcon(app.id)}
                          </div>
                          <Flex direction="column" gap="var(--space-1)" style={{ flex: 1 }}>
                            <h4 style={{ 
                              fontSize: 'var(--font-size-h2)', 
                              fontWeight: 'var(--font-weight-bold)', 
                              color: 'var(--text-primary)',
                              margin: 0
                            }}>
                              {app.name}
                            </h4>
                            <p style={{ 
                              fontSize: 'var(--font-size-body)', 
                              color: 'var(--text-secondary)',
                              margin: 0
                            }}>
                              {app.description}
                            </p>
                            <Flex align="center" gap="var(--space-2)" style={{ marginTop: 'var(--space-2)' }}>
                              <span style={{ 
                                fontSize: 'var(--font-size-fine)', 
                                color: 'var(--text-secondary)',
                                backgroundColor: 'var(--ui-surface-alt)',
                                padding: '2px 8px',
                                borderRadius: 'var(--border-radius-small)'
                              }}>
                                {app.category}
                              </span>
                              <span style={{ 
                                fontSize: 'var(--font-size-fine)', 
                                color: 'var(--text-muted)',
                                fontFamily: 'var(--font-family-code)'
                              }}>
                                ID: {app.id}
                              </span>
                            </Flex>
                          </Flex>
                          <Flex direction="column" gap="var(--space-1)">
                            {app.active ? (
                              <SuccessButton disabled>
                                ✅ Active
                              </SuccessButton>
                            ) : (
                              <PrimaryButton 
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  handleSwitchApp(app.id); 
                                }}
                                disabled={loading}
                              >
                                Activate
                              </PrimaryButton>
                            )}
                          </Flex>
                        </Flex>
                      </Card>
                    ))
                  ) : (
                    <p style={{ 
                      fontSize: 'var(--font-size-body)', 
                      color: 'var(--text-secondary)',
                      textAlign: 'center',
                      padding: 'var(--space-5)'
                    }}>
                      No applications are available
                    </p>
                  )}
                </Grid>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  {selectedApp ? `${getAppIcon(selectedApp.id)} ${selectedApp.name} Details` : 'ℹ️ App Information'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedApp ? (
                  <Flex direction="column" gap="var(--space-4)">
                    <div>
                      <h4 style={{ 
                        fontSize: 'var(--font-size-h2)', 
                        fontWeight: 'var(--font-weight-bold)', 
                        color: 'var(--text-primary)',
                        margin: '0 0 var(--space-2) 0'
                      }}>
                        📋 Description
                      </h4>
                      <p style={{ 
                        fontSize: 'var(--font-size-body)', 
                        color: 'var(--text-secondary)',
                        margin: 0
                      }}>
                        {selectedApp.description}
                      </p>
                    </div>

                    <div>
                      <h4 style={{ 
                        fontSize: 'var(--font-size-h2)', 
                        fontWeight: 'var(--font-weight-bold)', 
                        color: 'var(--text-primary)',
                        margin: '0 0 var(--space-2) 0'
                      }}>
                        🎯 Category
                      </h4>
                      <p style={{ 
                        fontSize: 'var(--font-size-body)', 
                        color: 'var(--text-secondary)',
                        margin: 0
                      }}>
                        {selectedApp.category}
                      </p>
                    </div>

                    <div>
                      <h4 style={{ 
                        fontSize: 'var(--font-size-h2)', 
                        fontWeight: 'var(--font-weight-bold)', 
                        color: 'var(--text-primary)',
                        margin: '0 0 var(--space-2) 0'
                      }}>
                        📊 Status
                      </h4>
                      <Flex direction="column" gap="var(--space-2)">
                        <span style={{ 
                          fontSize: 'var(--font-size-label)', 
                          color: selectedApp.active ? 'var(--state-success)' : 'var(--state-error)',
                          fontWeight: 'var(--font-weight-semibold)'
                        }}>
                          {selectedApp.active ? '🟢 Active' : '🔴 Inactive'}
                        </span>
                        <span style={{ 
                          fontSize: 'var(--font-size-fine)', 
                          color: 'var(--text-muted)',
                          fontFamily: 'var(--font-family-code)',
                          backgroundColor: 'var(--ui-surface-alt)',
                          padding: '4px 8px',
                          borderRadius: 'var(--border-radius-small)',
                          display: 'inline-block'
                        }}>
                          ID: {selectedApp.id}
                        </span>
                      </Flex>
                    </div>

                    <div>
                      <h4 style={{ 
                        fontSize: 'var(--font-size-h2)', 
                        fontWeight: 'var(--font-weight-bold)', 
                        color: 'var(--text-primary)',
                        margin: '0 0 var(--space-2) 0'
                      }}>
                        🔗 Quick Actions
                      </h4>
                      <Flex direction="column" gap="var(--space-2)">
                        <PrimaryButton
                          onClick={() => handleSwitchApp(selectedApp.id)}
                          disabled={loading || selectedApp.active}
                          style={{ width: '100%' }}
                        >
                          {selectedApp.active ? 'Currently Active' : 'Switch to App'}
                        </PrimaryButton>
                        <SecondaryButton
                          as="a"
                          href={`/${selectedApp.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ width: '100%', textAlign: 'center' }}
                        >
                          Open App
                        </SecondaryButton>
                      </Flex>
                    </div>
                  </Flex>
                ) : (
                  <p style={{ 
                    fontSize: 'var(--font-size-body)', 
                    color: 'var(--text-secondary)',
                    textAlign: 'center',
                    padding: 'var(--space-5)'
                  }}>
                    Select an application from the list to view detailed information
                  </p>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Card>
            <CardHeader>
              <CardTitle>💡 About the Apps Architecture</CardTitle>
            </CardHeader>
            <CardContent>
              <p style={{ 
                fontSize: 'var(--font-size-body)', 
                color: 'var(--text-secondary)',
                margin: '0 0 var(--space-3) 0',
                lineHeight: 1.6
              }}>
                This platform uses a multi-app architecture that allows you to run multiple 
                applications on the same serverless infrastructure. Each app is self-contained 
                but shares common resources like authentication, database, and UI components.
              </p>
              <ul style={{ 
                fontSize: 'var(--font-size-body)', 
                color: 'var(--text-secondary)',
                margin: 0,
                paddingLeft: 'var(--space-5)'
              }}>
                <li style={{ marginBottom: 'var(--space-2)' }}>
                  <strong>Cost Effective:</strong> All apps share the same AWS Free Tier resources
                </li>
                <li style={{ marginBottom: 'var(--space-2)' }}>
                  <strong>Scalable:</strong> Each app can be developed and deployed independently
                </li>
                <li style={{ marginBottom: 'var(--space-2)' }}>
                  <strong>Secure:</strong> Shared authentication and authorization system
                </li>
                <li>
                  <strong>Maintainable:</strong> Common components and services reduce duplication
                </li>
              </ul>
            </CardContent>
          </Card>
        </Flex>
      </Main>
    </Container>
  );
};

export default AdminApps;
